import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { handleError } from "src/Common/function";
import UserNotificationService from "src/Services/API/usernotification.service";
import {
  connectUserNotificationSocket,
  disconnectUserNotificationSocket,
} from "src/Services/socket/userNotificationSocket";
import NotificationList from "./NotificationList";
import { getNotificationPath, isRenderable } from "./notificationRenderers";

// Rows shown before the "View all notifications" footer is offered.
const INITIAL_VISIBLE = 10;

/**
 * The bell's slide-over. Owns the panel shell and the inbox it shows; the gear
 * hands off to the notification settings page, which is where the preferences
 * live. Reads the same endpoints the technician app uses; every row is scoped
 * to the authenticated user server-side.
 */
const NotificationDrawer = ({ open, onClose, onUnreadChange }) => {
  const navigate = useNavigate();
  // `mounted` keeps the panel in the tree for the length of the close
  // animation; `shown` drives the transform so it animates on the frame after
  // mount rather than snapping into place.
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const panelRef = useRef(null);
  // Until the first list lands, the empty `notifications` state says nothing
  // about the real unread count, so it must not be reported to the header.
  const hasLoadedRef = useRef(false);

  const unreadCount = notifications.filter((item) => !item.readAt).length;
  // The drawer only ever shows the most recent slice; the rest live on the
  // notifications page behind "View all notifications".
  const visibleNotifications = notifications.slice(0, INITIAL_VISIBLE);

  useEffect(() => {
    if (open) {
      setMounted(true);
      return undefined;
    }
    setShown(false);
    // Outlasts the 300ms transition so the panel isn't torn out mid-slide.
    const timer = setTimeout(() => setMounted(false), 320);
    return () => clearTimeout(timer);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !mounted || !panelRef.current) return undefined;
    // Reading a layout property resolves the off-screen `translate-x-full` as
    // the transition's start value; the frame after that is what it animates
    // away from.
    void panelRef.current.offsetWidth;
    const frame = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(frame);
  }, [open, mounted]);

  const loadNotifications = useCallback(async () => {
    try {
      const res = await UserNotificationService.list();
      setNotifications((res?.data ?? []).filter(isRenderable));
      hasLoadedRef.current = true;
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Every open is a fresh read: rows can be read or dismissed from the
  // notifications page in between, so stale content from a previous open would
  // be misleading.
  useEffect(() => {
    if (!open) return;
    setIsLoading(true);
    loadNotifications();
  }, [open, loadNotifications]);

  // While the drawer is open, rows arriving from the gateway are pulled in
  // immediately. No unsubscribe on cleanup: the socket is a shared singleton
  // and the room belongs to the connection, so leaving it here would also cut
  // off the header's badge.
  useEffect(() => {
    if (!open) return undefined;

    const socket = connectUserNotificationSocket();
    const subscribe = () => socket.emit("user-notifications:subscribe");
    const handleChanged = () => loadNotifications();

    socket.on("connect", subscribe);
    socket.on("user-notification-changed", handleChanged);
    if (socket.connected) subscribe();

    return () => {
      socket.off("connect", subscribe);
      socket.off("user-notification-changed", handleChanged);
      disconnectUserNotificationSocket();
    };
  }, [open, loadNotifications]);

  // Keep the header badge in step with what the drawer knows, so reading a row
  // updates the count without waiting for the next poll. Deliberately not gated
  // on `open`: clicking the last unread row marks it read and closes the drawer
  // in the same tick, and gating here meant that final count never reached the
  // badge. Gated on a completed load instead, so the empty initial state can't
  // blank a count the header already fetched.
  useEffect(() => {
    if (!hasLoadedRef.current) return;
    onUnreadChange?.(unreadCount);
  }, [unreadCount, onUnreadChange]);

  // Optimistic like the technician app: read state is not worth blocking the UI
  // over, and the next poll corrects any failure.
  const markRead = (item) => {
    setNotifications((prev) =>
      prev.map((row) => (row.id === item.id ? { ...row, readAt: new Date().toISOString() } : row))
    );
    UserNotificationService.markRead(item.id).catch(() => {});
  };

  const handleMarkAllRead = () => {
    const now = new Date().toISOString();
    setNotifications((prev) => prev.map((row) => (row.readAt ? row : { ...row, readAt: now })));
    UserNotificationService.markAllRead().catch(() => {});
  };

  // Preferences are a page of their own, so the gear leaves the drawer behind
  // rather than swapping the pane.
  const openSettings = () => {
    onClose?.();
    navigate("/notification-settings");
  };

  // The full inbox, with the filters and paging this panel has no room for.
  const openAllNotifications = () => {
    onClose?.();
    navigate("/notifications");
  };

  // Rows deep-link to whatever they are about — see getNotificationPath.
  const handleRowClick = (item) => {
    if (!item.readAt) markRead(item);

    const path = getNotificationPath(item);
    if (!path) return;
    onClose?.();
    navigate(path);
  };

  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-black transition-opacity duration-300 ease-out ${
          shown ? "bg-opacity-30" : "bg-opacity-0"
        }`}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Notifications"
        className={`relative h-full w-[420px] max-w-full bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          shown ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <NotificationList
          notifications={visibleNotifications}
          unreadCount={unreadCount}
          isLoading={isLoading}
          onViewAll={openAllNotifications}
          onRowClick={handleRowClick}
          onMarkAllRead={handleMarkAllRead}
          onOpenSettings={openSettings}
          onClose={onClose}
        />
      </div>
    </div>
  );
};

export default NotificationDrawer;
